import { Card, CardContent, Typography, Button, Box, Divider, Stack } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

import { UserAnswers } from '../../types';

import Tag from 'modules/common/components/MUI/Tag';
import { moneyFormat } from 'modules/common/display';

interface MonthlyExpenseCardProps {
    month: string;
    userAnswers: UserAnswers;
    homeOfficeAmount?: number;
    phoneInternetAmount?: number;
    mileageAmount?: number;
    isAutoFilled?: boolean;
    onFillOut?: () => void;
}

function MonthlyExpenseCard({
    month,
    userAnswers,
    homeOfficeAmount = 0,
    phoneInternetAmount = 0,
    mileageAmount = 0,
    isAutoFilled = false,
    onFillOut = () => {},
}: MonthlyExpenseCardProps) {
    // Determine which sections should show amounts vs N/A
    const showHomeOffice = userAnswers.usesHomeAsOffice === 'yes';
    const showPhoneInternet = userAnswers.usesHomeAsOffice === 'yes';
    const showMileage = userAnswers.drivesForBusiness === 'yes';

    // Calculate total only for sections that should show amounts
    const total =
        (showHomeOffice ? homeOfficeAmount : 0) +
        (showPhoneInternet ? phoneInternetAmount : 0) +
        (showMileage ? mileageAmount : 0);

    const hasExpenses = total > 0;

    // Show AUTO FILLED pill only when autofilled
    const statusBadge = isAutoFilled ? (
        <Tag
            icon={<CheckIcon />}
            label="AUTO FILLED"
            color="success"
            size="small"
            variant="filled"
            sx={{
                cursor: 'default', // No hover state
                '&:hover': {
                    backgroundColor: 'green.50', // Keep same background on hover
                },
            }}
        />
    ) : null;

    return (
        <Card
            sx={{
                flex: 1,
                minWidth: { xs: '100%', md: 200 },
                maxWidth: 400,
            }}>
            <CardContent sx={{ p: 3 }}>
                {/* Header section with title and status badge */}
                <Box sx={{ position: 'relative', mb: 2 }}>
                    <Typography variant="subtitle2">{month}</Typography>
                    {statusBadge && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                zIndex: 1,
                            }}>
                            {statusBadge}
                        </Box>
                    )}
                </Box>

                {/* Details section with dividers */}
                <Box
                    sx={{
                        py: 1,
                        mb: 3,
                    }}>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2" color={showHomeOffice ? 'text.primary' : 'text.disabled'}>
                            Home office
                        </Typography>
                        <Typography variant="subtitle2" color={showHomeOffice ? 'text.primary' : 'text.disabled'}>
                            {showHomeOffice ? moneyFormat(homeOfficeAmount, { minPrecision: 2 }) : 'N/A'}
                        </Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2" color={showPhoneInternet ? 'text.primary' : 'text.disabled'}>
                            Phone and internet
                        </Typography>
                        <Typography variant="subtitle2" color={showPhoneInternet ? 'text.primary' : 'text.disabled'}>
                            {showPhoneInternet ? moneyFormat(phoneInternetAmount, { minPrecision: 2 }) : 'N/A'}
                        </Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2" color={showMileage ? 'text.primary' : 'text.disabled'}>
                            Mileage
                        </Typography>
                        <Typography variant="subtitle2" color={showMileage ? 'text.primary' : 'text.disabled'}>
                            {showMileage ? moneyFormat(mileageAmount, { minPrecision: 2 }) : 'N/A'}
                        </Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    {/* Total row - same size as other rows */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                            Total
                        </Typography>
                        <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                            {moneyFormat(total, { minPrecision: 2 })}
                        </Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                </Box>

                {/* Action buttons */}
                <Stack direction="row" spacing={1}>
                    <Button variant={hasExpenses ? 'outlined' : 'contained'} color="onyx" fullWidth onClick={onFillOut}>
                        {hasExpenses ? 'Edit expenses' : 'Enter expenses'}
                    </Button>
                </Stack>
            </CardContent>
        </Card>
    );
}

export default MonthlyExpenseCard;
